"use client";

import MediaImage from "@/components/ui/MediaImage";

import Link from "next/link";
import PropertyLabel from "./ui/PropertyLabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/sharp-light-svg-icons";
import type { CaseData } from "@/cms/models";
import { getCaseUrl } from "@/utils/case-url";
import { getLabel } from "@/utils/case-label";
import { getClassicPropertyFacts } from "@/utils/classic-property-facts";
import { useEffect, useState } from "react";

export default function PropertyClassicCard({
  property,
  fallbackImageUrl,
}: {
  property: CaseData;
  fallbackImageUrl?: string;
}) {
  const image = property.images.find((image) =>
    image.namespace.endsWith("photo"),
  );
  const imageUrl = image?.lgUrl || image?.mdUrl || property.imageUrl;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [imageUrl]);
  const label = property.isSold ? "Solgt" : getLabel(property);
  return (
    <Link
      href={getCaseUrl(property)}
      className="re-classic-property-card"
      aria-label={property.advertisementAddressOrDisplayName}
    >
      <div className="re-classic-property-media">
        {imageUrl && !imageFailed ? (
          <MediaImage
            src={imageUrl}
            alt={property.advertisementAddressOrDisplayName}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            {fallbackImageUrl ? (
              <MediaImage
                src={fallbackImageUrl}
                alt="Billede på vej"
                className="re-classic-property-placeholder"
              />
            ) : (
              "Billede på vej"
            )}
          </div>
        )}
        {label && (
          <PropertyLabel
            legacy={<span className="re-classic-property-label">{label}</span>}
          >
            {label}
          </PropertyLabel>
        )}
      </div>
      <div className="re-classic-property-copy">
        <h2>
          {property.shortAddress || property.advertisementAddressOrDisplayName}
          <br />
          {property.zipAndCity}
        </h2>
        <div className="re-classic-property-bottom">
          <dl className="re-classic-property-facts">
            {getClassicPropertyFacts(property).map((fact) => (
              <div key={fact.key}>
                <dt className="inline">{fact.label}: </dt>
                <dd className="inline">{fact.value}</dd>
              </div>
            ))}
          </dl>
          {property.isSold ? (
            <span>Solgt</span>
          ) : (
            <span className="re-action-arrow">
              Se ejendom
              <FontAwesomeIcon icon={faChevronRight} aria-hidden />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
